import { Card, IconTile, useToast } from "@design";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Database, FileUp, Globe, NotebookPen } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router";
import { z } from "zod";
import { createDataSourceMutation, errorMessage, listDataSourcesQueryKey, schemas } from "@/api";
import { useAreaCrumbs } from "@/components/areaCrumbs";
import { useAppForm } from "@/components/form/form";
import { PageBreadcrumbs } from "@/components/PageBreadcrumbs";
import { Text } from "@/components/Text";
import { type Capabilities, useCapabilities, useCurrentUser } from "@/features/account/useCurrentUser";
import { SharePointPicker, type SharePointSelection } from "./SharePointPicker";
import { sharePointConfig } from "./sharePointConfig";

const TYPES = ["upload", "website", "sharepoint", "notes"] as const;
type SourceType = (typeof TYPES)[number];

/** Types that need a deployment capability (SharePoint only exists where Microsoft Graph is available). */
const TYPE_CAPABILITY: Partial<Record<SourceType, keyof Capabilities>> = { sharepoint: "sharepointEnabled" };

export function availableTypes(caps: Capabilities): SourceType[] {
  return TYPES.filter((type) => {
    const capability = TYPE_CAPABILITY[type];
    return !capability || caps[capability];
  });
}

const create = schemas.zDataCollectionCreate.shape;
const createSchema = z.object({
  name: create.name,
  description: create.description,
  // Type-specific: website URL, note content. Visibility is set later on the
  // Access tab, not at create time.
  url: z.string(),
  content: create.content,
});
type CreateValues = z.infer<typeof createSchema>;

/** A Build with AI draft opened in the create form ("Open in form" on its card). */
export interface DataSourceFormState {
  draft: { name?: string; description?: string; url?: string; content?: string | null };
  /** The chat the draft came from; the created source is linked to it. */
  sourceSessionUuid?: string;
}

/**
 * /build/data-sources/new — type picker first, then the type-specific form.
 * SharePoint uses the real Graph browser picker (`SharePointPicker`): pick a
 * site → a document library → browse folders and select files or a folder;
 * the rest (upload/website/notes) create directly. SharePoint is offered only
 * when the deployment has it (`sharepointEnabled`); `/new/sharepoint` otherwise
 * falls back to the type picker.
 */
export function NewDataSourcePage() {
  const { t } = useTranslation(["dataSources"]);
  const areas = useAreaCrumbs();
  const navigate = useNavigate();
  const { type } = useParams();
  const types = availableTypes(useCapabilities());
  const active: SourceType | undefined = (types as readonly string[]).includes(type ?? "")
    ? (type as SourceType)
    : undefined;

  return (
    <div className="flex flex-col">
      <PageBreadcrumbs
        trail={[areas.build, { label: t("dataSources:title"), to: "/build/data-sources" }]}
        current={t("dataSources:create.title")}
      />
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-4 py-6 md:px-8">
        {active ? (
          <TypeForm type={active} />
        ) : (
          <>
            <Text variant="h3">{t("dataSources:create.chooseType")}</Text>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {types.map((value) => (
                <TypeCard key={value} type={value} onClick={() => navigate(`/build/data-sources/new/${value}`)} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const TYPE_ICON: Record<SourceType, React.ReactNode> = {
  upload: <FileUp size={18} />,
  website: <Globe size={18} />,
  sharepoint: <Database size={18} />,
  notes: <NotebookPen size={18} />,
};

function TypeCard({ type, onClick }: Readonly<{ type: SourceType; onClick: () => void }>) {
  const { t } = useTranslation("dataSources");
  return (
    <Card onClick={onClick} size="md" aria-label={t(`types.${type}`)}>
      <div className="flex flex-col gap-2">
        <IconTile variant="brand">{TYPE_ICON[type]}</IconTile>
        <Text variant="h3">{t(`types.${type}`)}</Text>
        <Text variant="body-sm" tone="secondary">
          {t(`types.${type}Desc`)}
        </Text>
      </div>
    </Card>
  );
}

function TypeForm({ type }: Readonly<{ type: SourceType }>) {
  const { t } = useTranslation("dataSources");
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const me = useCurrentUser();
  const fromChat = (useLocation().state as DataSourceFormState | null) ?? undefined;
  const seed = fromChat?.draft;
  const createMutation = useMutation(createDataSourceMutation());
  const [spSelection, setSpSelection] = useState<SharePointSelection | null>(null);

  const form = useAppForm({
    defaultValues: {
      name: seed?.name ?? "",
      description: seed?.description ?? "",
      url: seed?.url ?? "",
      content: seed?.content ?? "",
    } as CreateValues,
    validators: { onChange: createSchema },
    onSubmit: async ({ value }) => {
      if (type === "sharepoint" && !spSelection) {
        toast.error(t("create.sharepointRequired"));
        return;
      }
      const teamUuid = me.data?.teams.find((tm) => tm.team.isEveryone)?.team.uuid ?? me.data?.teams[0]?.team.uuid;
      if (!teamUuid) {
        toast.error(t("form.createFailed"));
        return;
      }
      try {
        const created = await createMutation.mutateAsync({
          body: {
            name: value.name,
            description: value.description || null,
            teamUuid,
            sourceType: type,
            ...(type === "website" && value.url ? { config: { urls: [value.url] } } : {}),
            ...(type === "notes" && value.content ? { content: value.content } : {}),
            ...(type === "sharepoint" && spSelection ? { config: sharePointConfig(spSelection) } : {}),
            sourceSessionUuid: fromChat?.sourceSessionUuid,
          },
          throwOnError: true,
        });
        await queryClient.invalidateQueries({ queryKey: listDataSourcesQueryKey() });
        toast.success(t("form.created"));
        navigate(`/build/data-sources/${created.data.uuid}`);
      } catch (error) {
        toast.error(errorMessage(error, t("form.createFailed")));
      }
    },
  });

  return (
    <Card>
      <form
        aria-label={t("form.label")}
        className="flex flex-col gap-5"
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit();
        }}
      >
        <div className="flex items-center gap-2 text-content-secondary">
          <IconTile variant="brand">{TYPE_ICON[type]}</IconTile>
          <Text variant="body">{t(`types.${type}`)}</Text>
        </div>
        <form.AppField name="name">
          {(field) => <field.TextField label={t("form.name")} required placeholder={t("form.namePlaceholder")} />}
        </form.AppField>
        <form.AppField name="description">
          {(field) => (
            <field.TextareaField
              label={t("form.description")}
              placeholder={t("form.descriptionPlaceholder")}
              rows={2}
            />
          )}
        </form.AppField>

        {type === "website" && (
          <form.AppField name="url">
            {(field) => <field.TextField label={t("create.url")} placeholder={t("create.urlPlaceholder")} />}
          </form.AppField>
        )}
        {type === "notes" && (
          <form.AppField name="content">
            {(field) => (
              <field.TextareaField
                label={t("create.noteContent")}
                placeholder={t("create.noteContentPlaceholder")}
                rows={8}
              />
            )}
          </form.AppField>
        )}
        {type === "sharepoint" && <SharePointPicker onSelect={setSpSelection} />}

        <form.AppForm>
          <form.SubmitActions
            onCancel={() => navigate("/build/data-sources")}
            submitLabel={t("form.create")}
            requireChanges={false}
          />
        </form.AppForm>
      </form>
    </Card>
  );
}
